import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import axios from 'axios'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import './ResetPassword.css'

const LOGO_URL = 'https://res.cloudinary.com/dk50cmtps/image/upload/v1786338929/unnamed_efi40j.png'
const LOGO_SIDE_IMG_URL = 'https://res.cloudinary.com/dk50cmtps/image/upload/v1786336638/592f1aee-a328-4135-bd3f-8a1e20477346_smm1fr.png'

// const API_BASE_URL = 'https://upskledbackend.vercel.app/'
const API_BASE_URL = 'https://upskld.co/api'

const resetSchema = Yup.object({
  password: Yup.string()
    .min(6, 'Password must be at least 6 characters')
    .required('Password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password')], 'Passwords do not match')
    .required('Please confirm your password'),
})

const ResetPassword = () => {
  const navigate = useNavigate()
  const { token } = useParams()

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isReset, setIsReset] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const formik = useFormik({
    initialValues: {
      password: '',
      confirmPassword: '',
    },
    validationSchema: resetSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true)
      try {
        const response = await axios.post(
          `${API_BASE_URL}/reset-password/${token}`,
          { password: values.password }
        )

        if (response.data.success) {
          toast.success(response.data.message || 'Password reset successful!')
          setIsReset(true)
          setTimeout(() => {
            navigate('/login')
          }, 1800)
        }
      } catch (error) {
        const errorMessage =
          error.response?.data?.message ||
          'Something went wrong. Please try again.'
        toast.error(errorMessage)
      } finally {
        setIsSubmitting(false)
      }
    },
  })

  const goToLogin = (e) => {
    e.preventDefault()
    navigate('/login')
  }

  const EyeIcon = ({ open }) =>
    open ? (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-10-8-10-8a18.6 18.6 0 0 1 4.22-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 10 8 10 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </svg>
    ) : (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s3-8 11-8 11 8 11 8-3 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    )

  return (
    <div className="reset_pg_container">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="reset_pg_left">
        <div className="reset_pg_logo_row">
          <img
            src={LOGO_SIDE_IMG_URL}
            alt="Rubicr HR"
            className="reset_pg_logo_side_img"
          />
          <img
            src={LOGO_URL}
            alt="Rubicr"
            className="reset_pg_logo_img"
          />
        </div>
      </div>

      <div className="reset_pg_right">
        <div className="reset_pg_form_wrap">
          {!isReset ? (
            <>
              <h2 className="reset_pg_title">Set a new password</h2>
              <p className="reset_pg_subtitle">
                Your new password must be different from previously used passwords.
              </p>

              <form className="reset_pg_form" onSubmit={formik.handleSubmit} noValidate>
                <div className="reset_pg_field">
                  <label className="reset_pg_label" htmlFor="password">
                    New Password <span className="reset_pg_required">*</span>
                  </label>
                  <div className="reset_pg_password_wrap">
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      className="reset_pg_input reset_pg_input_password"
                      placeholder="Enter new password"
                      value={formik.values.password}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    <button
                      type="button"
                      className="reset_pg_eye_btn"
                      onClick={() => setShowPassword((prev) => !prev)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      tabIndex={-1}
                    >
                      <EyeIcon open={showPassword} />
                    </button>
                  </div>
                  {formik.touched.password && formik.errors.password && (
                    <span className="reset_pg_error_text">{formik.errors.password}</span>
                  )}
                </div>

                <div className="reset_pg_field">
                  <label className="reset_pg_label" htmlFor="confirmPassword">
                    Confirm Password <span className="reset_pg_required">*</span>
                  </label>
                  <div className="reset_pg_password_wrap">
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      className="reset_pg_input reset_pg_input_password"
                      placeholder="Re-enter new password"
                      value={formik.values.confirmPassword}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    <button
                      type="button"
                      className="reset_pg_eye_btn"
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      tabIndex={-1}
                    >
                      <EyeIcon open={showConfirmPassword} />
                    </button>
                  </div>
                  {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                    <span className="reset_pg_error_text">{formik.errors.confirmPassword}</span>
                  )}
                </div>

                <button type="submit" className="reset_pg_submit_btn" disabled={isSubmitting}>
                  {isSubmitting ? 'Resetting...' : (
                    <>
                      Reset Password <span className="reset_pg_arrow">&rarr;</span>
                    </>
                  )}
                </button>

                <p className="reset_pg_back_text">
                  Remembered your password?{' '}
                  <a href="/login" className="reset_pg_back_link" onClick={goToLogin}>
                    Back to Login
                  </a>
                </p>
              </form>
            </>
          ) : (
            <div className="reset_pg_success_wrap">
              <h2 className="reset_pg_title">Password reset successful!</h2>
              <p className="reset_pg_subtitle">
                Redirecting you to the login page...
              </p>
              <button
                type="button"
                className="reset_pg_submit_btn"
                onClick={goToLogin}
              >
                Go to Login
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ResetPassword